import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from "react"
import { useAuth } from "@/lib/auth-context"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { API_BASE } from '@/lib/api-base'
import {
  Plus, Pencil, Trash2, Phone, Mail, MapPin, Building2,
  TrendingDown, TrendingUp, Package, History,
  X
} from "lucide-react"
import {
  MASK_MAX_LENGTH,
  cepError,
  cnpjError,
  cpfError,
  maskCep,
  maskCnpj,
  maskCpf,
  maskNumero,
  maskPhone,
  onlyDigits,
  optionalPhoneError,
  phoneError,
} from "@/lib/br-masks"
import { formatBRL } from "@/lib/money"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { SetupModuleEmptyCta } from "@/components/setup-module-empty-cta"

interface Fornecedor {
  id: string
  nome: string
  tipo_pessoa: "PF" | "PJ"
  documento: string
  telefone: string
  telefone_secundario: string | null
  email: string | null
  contato_nome: string
  cidade: string
  estado: string
  bairro: string | null
  logradouro: string | null
  numero: string | null
  cep: string | null
  itens_fornecidos: string[]
  status: string
  criado_em: string
}

interface ItemFornecedor {
  id: string
  fornecedor_id: string
  item_estoque_id: string
  item_nome: string
  preco_atual: number
  preco_anterior: number | null
  variacao_percentual: number | null
  ultima_atualizacao: string
}

const ESTADOS_BRASIL = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO"
]

const ITENS_COMUNS = [
  "Racao inicial",
  "Racao crescimento", 
  "Racao postura",
  "Racao frango",
  "Medicamentos",
  "Vitaminas",
  "Vacinas",
  "Material avicola",
  "Equipamentos",
  "Embalagens ovos",
  "Bandejas",
  "Servicos",
  "Outros"
]

function FornecedoresPage() {
  const apiBase = API_BASE
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([])
  const [itensFornecedor, setItensFornecedor] = useState<ItemFornecedor[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editFornecedor, setEditFornecedor] = useState<Fornecedor | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [detailFornecedorId, setDetailFornecedorId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  
  const [form, setForm] = useState({
    nome: "",
    tipo_pessoa: "PJ" as "PF" | "PJ",
    documento: "",
    telefone: "",
    telefone_secundario: "",
    email: "",
    contato_nome: "",
    cidade: "",
    estado: "",
    bairro: "",
    logradouro: "",
    numero: "",
    cep: "",
    itens_fornecidos: [] as string[],
  })

  const [novoItem, setNovoItem] = useState("")

  const fetchFornecedores = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/fornecedores/`)
      if (res.ok) {
        setFornecedores(await res.json())
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }, [apiFetch, apiBase])

  const fetchItensFornecedor = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBase}/api/fornecedores/itens`)
      if (res.ok) {
        setItensFornecedor(await res.json())
      }
    } catch (e) {
      console.error(e)
      setLoadError("Não foi possível carregar os dados. Tente novamente.")
    }
  }, [apiFetch, apiBase])

  const retryLoad = () => {
    setLoadError(null)
    void fetchFornecedores()
    void fetchItensFornecedor()
  }

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    void fetchFornecedores()
    void fetchItensFornecedor()
  }, [user, isSuperAdmin, selectedTenantId, fetchFornecedores, fetchItensFornecedor])

  const handleAddItem = () => {
    if (novoItem && !form.itens_fornecidos.includes(novoItem)) {
      setForm({ ...form, itens_fornecidos: [...form.itens_fornecidos, novoItem] })
      setNovoItem("")
    }
  }

  const handleRemoveItem = (item: string) => {
    setForm({ ...form, itens_fornecidos: form.itens_fornecidos.filter(i => i !== item) })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!form.nome.trim()) {
      setSubmitError("Nome e obrigatorio.")
      return
    }
    if (!form.documento.trim()) {
      setSubmitError(form.tipo_pessoa === "PJ" ? "CNPJ e obrigatorio." : "CPF e obrigatorio.")
      return
    }
    const docErr = form.tipo_pessoa === "PJ" ? cnpjError(form.documento) : cpfError(form.documento)
    if (docErr) {
      setSubmitError(docErr)
      return
    }
    const telErr = phoneError(form.telefone)
    if (telErr) {
      setSubmitError(telErr)
      return
    }
    const telSecErr = optionalPhoneError(form.telefone_secundario)
    if (telSecErr) {
      setSubmitError(telSecErr)
      return
    }
    const cepErr = cepError(form.cep)
    if (cepErr) {
      setSubmitError(cepErr)
      return
    }
    if (!form.contato_nome.trim()) {
      setSubmitError("Nome do contato e obrigatorio.")
      return
    }
    if (!form.cidade.trim()) {
      setSubmitError("Cidade e obrigatoria.")
      return
    }
    if (!form.estado) {
      setSubmitError("Estado e obrigatorio.")
      return
    }
    if (form.itens_fornecidos.length === 0) {
      setSubmitError("Selecione ao menos um item que o fornecedor fornece.")
      return
    }
    
    setSubmitError(null)
    
    const payload = {
      nome: form.nome.trim(),
      tipo_pessoa: form.tipo_pessoa,
      documento: onlyDigits(form.documento),
      telefone: onlyDigits(form.telefone),
      telefone_secundario: onlyDigits(form.telefone_secundario) || null,
      email: form.email.trim() || null,
      contato_nome: form.contato_nome.trim(),
      cidade: form.cidade.trim(),
      estado: form.estado,
      bairro: form.bairro.trim() || null,
      logradouro: form.logradouro.trim() || null,
      numero: form.numero.trim() || null,
      cep: onlyDigits(form.cep) || null,
      itens_fornecidos: form.itens_fornecidos,
    }

    try {
      const url = editFornecedor 
        ? `${apiBase}/api/fornecedores/${editFornecedor.id}`
        : `${apiBase}/api/fornecedores/`
      const res = await apiFetch(url, {
        method: editFornecedor ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        setShowForm(false)
        setEditFornecedor(null)
        setForm({
          nome: "", tipo_pessoa: "PJ", documento: "", telefone: "", telefone_secundario: "",
          email: "", contato_nome: "", cidade: "", estado: "", bairro: "", logradouro: "",
          numero: "", cep: "", itens_fornecidos: [],
        })
        fetchFornecedores()
      } else {
        const errData = await res.json().catch(() => ({})) as { detail?: string }
        setSubmitError(typeof errData?.detail === "string" ? errData.detail : "Erro ao salvar.")
      }
    } catch (e) {
      console.error(e)
      setSubmitError("Erro de conexao.")
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este fornecedor?")) return
    try {
      const res = await apiFetch(`${apiBase}/api/fornecedores/${id}`, { method: "DELETE" })
      if (res.ok) {
        fetchFornecedores()
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleToggleStatus = async (fornecedor: Fornecedor) => {
    const novoStatus = fornecedor.status === "ATIVO" ? "INATIVO" : "ATIVO"
    try {
      const res = await apiFetch(`${apiBase}/api/fornecedores/${fornecedor.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: novoStatus }),
      })
      if (res.ok) {
        fetchFornecedores()
      }
    } catch (e) {
      console.error(e)
    }
  }

  const formatDocumento = (doc: string, tipo: "PF" | "PJ") => {
    return tipo === "PJ" ? maskCnpj(doc) : maskCpf(doc)
  }

  const itensPorFornecedor = useMemo(() => {
    const map = new Map<string, ItemFornecedor[]>()
    for (const item of itensFornecedor) {
      const lista = map.get(item.fornecedor_id) || []
      lista.push(item)
      map.set(item.fornecedor_id, lista)
    }
    return map
  }, [itensFornecedor])

  const getItensDoFornecedor = (fornecedorId: string) => itensPorFornecedor.get(fornecedorId) || []

  const filteredFornecedores = useMemo(
    () =>
      fornecedores.filter(
        f =>
          f.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          f.documento.includes(searchTerm.replace(/\D/g, "")) ||
          f.itens_fornecidos.some(item => item.toLowerCase().includes(searchTerm.toLowerCase())),
      ),
    [fornecedores, searchTerm],
  )

  const melhorPrecoPorItem = useMemo(() => {
    const map = new Map<string, ItemFornecedor>()
    for (const item of itensFornecedor) {
      const atual = map.get(item.item_nome)
      if (!atual || item.preco_atual < atual.preco_atual) map.set(item.item_nome, item)
    }
    return map
  }, [itensFornecedor])

  const getMelhorPreco = (itemNome: string) => melhorPrecoPorItem.get(itemNome) || null

  const detailFornecedor = detailFornecedorId ? fornecedores.find(f => f.id === detailFornecedorId) : null
  const itensDoFornecedorDetalhe = detailFornecedorId ? getItensDoFornecedor(detailFornecedorId) : []

  if (isSuperAdmin && !selectedTenantId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Fornecedores</h1>
          <p className="text-muted-foreground text-sm">Gerencie fornecedores e historico de precos</p>
        </div>
        <Button onClick={() => {
          setSubmitError(null)
          setShowForm(true)
          setEditFornecedor(null)
          setForm({
            nome: "", tipo_pessoa: "PJ", documento: "", telefone: "", telefone_secundario: "",
            email: "", contato_nome: "", cidade: "", estado: "", bairro: "", logradouro: "",
            numero: "", cep: "", itens_fornecidos: [],
          })
        }}>
          <Plus className="h-4 w-4 mr-2" /> Novo Fornecedor
        </Button>
      </div>

      {loadError && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm flex items-center justify-between gap-2">
          <span>{loadError}</span>
          <Button type="button" variant="outline" size="sm" onClick={retryLoad}>
            Tentar novamente
          </Button>
        </div>
      )}

      <SetupModuleEmptyCta
        module="fornecedores"
        label="Fornecedores"
        href="/fornecedores"
        onAdd={() => {
          setSubmitError(null)
          setShowForm(true)
          setEditFornecedor(null)
          setForm({
            nome: "", tipo_pessoa: "PJ", documento: "", telefone: "", telefone_secundario: "",
            email: "", contato_nome: "", cidade: "", estado: "", bairro: "", logradouro: "",
            numero: "", cep: "", itens_fornecidos: [],
          })
        }}
      />

      <div>
        <Input
          placeholder="Buscar por nome, documento ou item..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) {
            setShowForm(false)
            setEditFornecedor(null)
            setSubmitError(null)
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">
            {editFornecedor ? 'Editar Fornecedor' : 'Novo Fornecedor'}
          </DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">
              {editFornecedor ? 'Editar' : 'Novo'} Fornecedor
            </h2>
            {submitError && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                {submitError}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                    value={form.tipo_pessoa}
                    onChange={(e) => setForm({ ...form, tipo_pessoa: e.target.value as "PF" | "PJ", documento: "" })}
                  >
                    <option value="PJ">Pessoa Juridica (CNPJ)</option>
                    <option value="PF">Pessoa Fisica (CPF)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Nome / Razao Social *</Label>
                  <Input
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    maxLength={MASK_MAX_LENGTH.nome}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>{form.tipo_pessoa === "PJ" ? "CNPJ" : "CPF"} *</Label>
                  <Input
                    value={form.documento}
                    onChange={(e) => setForm({
                      ...form,
                      documento: form.tipo_pessoa === "PJ" ? maskCnpj(e.target.value) : maskCpf(e.target.value),
                    })}
                    placeholder={form.tipo_pessoa === "PJ" ? "00.000.000/0000-00" : "000.000.000-00"}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={form.tipo_pessoa === "PJ" ? MASK_MAX_LENGTH.cnpj : MASK_MAX_LENGTH.cpf}
                    required
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Nome do Contato *</Label>
                  <Input
                    value={form.contato_nome}
                    onChange={(e) => setForm({ ...form, contato_nome: e.target.value })}
                    placeholder="Pessoa para contato"
                    maxLength={MASK_MAX_LENGTH.nome}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone *</Label>
                  <Input
                    value={form.telefone}
                    onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })}
                    placeholder="(00) 00000-0000"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={MASK_MAX_LENGTH.phone}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone Secundario</Label>
                  <Input
                    value={form.telefone_secundario}
                    onChange={(e) => setForm({ ...form, telefone_secundario: maskPhone(e.target.value) })}
                    placeholder="(00) 00000-0000"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={MASK_MAX_LENGTH.phone}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="contato@empresa.com"
                  maxLength={MASK_MAX_LENGTH.email}
                />
              </div>

              <div className="border-t pt-4">
                <Label className="text-base font-semibold">Endereco *</Label>
                <p className="text-xs text-muted-foreground mb-3">Preencha pelo menos cidade e estado</p>
                <div className="grid gap-4 md:grid-cols-4">
                  <div className="space-y-2">
                    <Label>CEP</Label>
                    <Input
                      value={form.cep}
                      onChange={(e) => setForm({ ...form, cep: maskCep(e.target.value) })}
                      placeholder="00000-000"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      maxLength={MASK_MAX_LENGTH.cep}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Estado *</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                      value={form.estado}
                      onChange={(e) => setForm({ ...form, estado: e.target.value })}
                      required
                    >
                      <option value="">Selecione</option>
                      {ESTADOS_BRASIL.map(uf => (
                        <option key={uf} value={uf}>{uf}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Cidade *</Label>
                    <Input
                      value={form.cidade}
                      onChange={(e) => setForm({ ...form, cidade: e.target.value })}
                      maxLength={MASK_MAX_LENGTH.cidade}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bairro</Label>
                    <Input
                      value={form.bairro}
                      onChange={(e) => setForm({ ...form, bairro: e.target.value })}
                      maxLength={MASK_MAX_LENGTH.bairro}
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-4 mt-4">
                  <div className="space-y-2 md:col-span-3">
                    <Label>Logradouro</Label>
                    <Input
                      value={form.logradouro}
                      onChange={(e) => setForm({ ...form, logradouro: e.target.value })}
                      placeholder="Rua, Avenida..."
                      maxLength={MASK_MAX_LENGTH.logradouro}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Numero</Label>
                    <Input
                      value={form.numero}
                      onChange={(e) => setForm({ ...form, numero: maskNumero(e.target.value) })}
                      maxLength={MASK_MAX_LENGTH.numero}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-base font-semibold">Itens que Fornece *</Label>
                <p className="text-xs text-muted-foreground mb-3">Selecione os produtos/servicos que este fornecedor oferece</p>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {ITENS_COMUNS.map(item => (
                    <Button
                      key={item}
                      type="button"
                      variant={form.itens_fornecidos.includes(item) ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (form.itens_fornecidos.includes(item)) {
                          setForm({ ...form, itens_fornecidos: form.itens_fornecidos.filter(i => i !== item) })
                        } else {
                          setForm({ ...form, itens_fornecidos: [...form.itens_fornecidos, item] })
                        }
                      }}
                    >
                      {item}
                    </Button>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Input
                    value={novoItem}
                    onChange={(e) => setNovoItem(e.target.value)}
                    placeholder="Outro item..."
                    className="max-w-xs"
                  />
                  <Button type="button" variant="outline" onClick={handleAddItem}>Adicionar</Button>
                </div>

                {form.itens_fornecidos.filter(i => !ITENS_COMUNS.includes(i)).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {form.itens_fornecidos.filter(i => !ITENS_COMUNS.includes(i)).map(item => (
                      <Badge key={item} variant="secondary" className="flex items-center gap-1">
                        {item}
                        <button type="button" aria-label={`Remover item ${item}`} onClick={() => handleRemoveItem(item)} className="ml-1 hover:text-destructive">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit">Salvar</Button>
                <Button variant="outline" type="button" onClick={() => { setShowForm(false); setEditFornecedor(null); setSubmitError(null) }}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4">
        {filteredFornecedores.map((f) => {
          const itens = getItensDoFornecedor(f.id)
          
          return (
            <Card key={f.id}>
              <CardHeader>
                <CardTitle className="flex justify-between items-center gap-2">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <span>{f.nome}</span>
                    <Badge variant={f.status === "ATIVO" ? "default" : "secondary"}>
                      {f.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(f)}>
                      {f.status === "ATIVO" ? "Desativar" : "Ativar"}
                    </Button>
                    <Button variant="ghost" size="icon" aria-label={`Editar fornecedor ${f.nome}`} onClick={() => {
                      setEditFornecedor(f)
                      setForm({
                        nome: f.nome,
                        tipo_pessoa: f.tipo_pessoa,
                        documento: f.tipo_pessoa === "PJ" ? maskCnpj(f.documento) : maskCpf(f.documento),
                        telefone: maskPhone(f.telefone),
                        telefone_secundario: f.telefone_secundario ? maskPhone(f.telefone_secundario) : "",
                        email: f.email || "",
                        contato_nome: f.contato_nome,
                        cidade: f.cidade,
                        estado: f.estado,
                        bairro: f.bairro || "",
                        logradouro: f.logradouro || "",
                        numero: maskNumero(f.numero || ""),
                        cep: f.cep ? maskCep(f.cep) : "",
                        itens_fornecidos: f.itens_fornecidos || [],
                      })
                      setShowForm(true)
                    }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" aria-label={`Excluir fornecedor ${f.nome}`} onClick={() => handleDelete(f.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1 text-sm">
                    <p className="text-muted-foreground">
                      {f.tipo_pessoa === "PJ" ? "CNPJ" : "CPF"}: {formatDocumento(f.documento, f.tipo_pessoa)}
                    </p>
                    <p className="flex items-center gap-1">
                      <Phone className="h-3 w-3 text-muted-foreground" />
                      {maskPhone(f.telefone)}
                      {f.telefone_secundario && <span className="text-muted-foreground"> | {maskPhone(f.telefone_secundario)}</span>}
                    </p>
                    <p className="text-muted-foreground">Contato: {f.contato_nome}</p>
                    {f.email && (
                      <p className="flex items-center gap-1 text-muted-foreground">
                        <Mail className="h-3 w-3" /> {f.email}
                      </p>
                    )}
                    <p className="flex items-center gap-1 text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {[f.cidade, f.estado].filter(Boolean).join(" - ")}
                      {f.bairro && ` (${f.bairro})`}
                    </p>
                    <div className="flex flex-wrap gap-1 pt-1">
                      {f.itens_fornecidos?.slice(0, 4).map(item => (
                        <Badge key={item} variant="outline" className="text-xs">{item}</Badge>
                      ))}
                      {f.itens_fornecidos?.length > 4 && (
                        <Badge variant="outline" className="text-xs">+{f.itens_fornecidos.length - 4}</Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex shrink-0 items-center gap-2"
                    onClick={() => setDetailFornecedorId(f.id)}
                  >
                    <Package className="h-4 w-4" />
                    <span>Precos</span>
                    {itens.length > 0 && <Badge variant="secondary">{itens.length}</Badge>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {filteredFornecedores.length === 0 && (
        <p className="text-muted-foreground text-center py-8">
          {searchTerm ? "Nenhum fornecedor encontrado." : "Nenhum fornecedor cadastrado."}
        </p>
      )}

      <Dialog open={!!detailFornecedor} onOpenChange={(open) => { if (!open) setDetailFornecedorId(null) }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">
            {detailFornecedor ? `Historico de precos — ${detailFornecedor.nome}` : "Historico de precos"}
          </DialogTitle>
          {detailFornecedor && (
            <div className="p-6 space-y-4">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2 pr-10">
                  <History className="h-5 w-5" />
                  Historico de Precos
                </h2>
                <p className="text-sm text-muted-foreground">{detailFornecedor.nome}</p>
              </div>

              {itensDoFornecedorDetalhe.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  Nenhum preco registrado. Os precos aparecerao aqui apos realizar compras deste fornecedor.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {itensDoFornecedorDetalhe.map((item) => {
                    const melhor = getMelhorPreco(item.item_nome)
                    const isMelhor = melhor?.id === item.id

                    return (
                      <div
                        key={item.id}
                        className={`p-3 rounded-lg border ${isMelhor ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : 'bg-muted/30'}`}
                      >
                        <p className="font-medium">{item.item_nome}</p>
                        <p className="text-lg font-bold">{formatBRL(item.preco_atual)}</p>
                        {item.variacao_percentual !== null && (
                          <p className={`text-xs flex items-center gap-1 ${item.variacao_percentual < 0 ? 'text-green-600' : item.variacao_percentual > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                            {item.variacao_percentual < 0 ? <TrendingDown className="h-3 w-3" /> : item.variacao_percentual > 0 ? <TrendingUp className="h-3 w-3" /> : null}
                            {item.variacao_percentual > 0 ? '+' : ''}{item.variacao_percentual.toFixed(1)}%
                          </p>
                        )}
                        {isMelhor && itensFornecedor.filter(i => i.item_nome === item.item_nome).length > 1 && (
                          <Badge variant="default" className="text-xs mt-1">Melhor preco</Badge>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/fornecedores')({
  component: FornecedoresPage,
})
